import { client } from '@/api/common';
import {
  API,
  BASE,
  URL_ACCOUNTS,
  URL_CONNECT,
  URL_RECONNECT,
  URL_SYNC,
  URL_TENANTS,
  V1,
} from '@/lib/constants/urls.ts';
import type { Account } from '@repo/database/browser';
import type {
  BaseFilterDto,
  ConnectRequestDto,
  ConnectResponse,
  CreateAccountDto,
  ListResponse,
  ReconnectResponse,
  UpdateAccountDto,
} from '@repo/api';
import type { AddNullToUndefined } from '@repo/utils';
import { ACCOUNTS, PROTECTED } from '@/lib/constants/routes';
import { runtimeConfig } from '@/lib/runtime-config';

export type RestoreBankFieldInput =
  | 'creditLimit'
  | 'overdraftExpiresAt'
  | 'linkedPortfolioAccountId'
  | 'linkedBankAccountId';

export interface RestoreBankFieldsPayload {
  fields: RestoreBankFieldInput[];
}

export const fetchAccount = async (
  tenantId: string,
  id?: string
): Promise<Account | undefined> => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}/${id}`,
    method: 'GET',
  });

  return response.data;
};

export const syncAccount = async (
  tenantId: string,
  uuid: string
): Promise<Account | undefined> => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}${URL_SYNC}/${uuid}`,
    method: 'GET',
  });

  return response.data;
};

export const connectAccount = async (
  tenantId: string,
  connectParams?: ConnectRequestDto
): Promise<ConnectResponse> => {
  const clientUrl = runtimeConfig.VITE_CLIENT_URL;
  const data = {
    ...connectParams,
    returnUrl: clientUrl ? `${clientUrl}${PROTECTED}${ACCOUNTS}` : undefined,
  };
  const response = await client<ConnectResponse>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}${URL_CONNECT}`,
    method: 'POST',
    data,
  });

  return response.data;
};

export const reconnectAccount = async (
  tenantId: string,
  accountId: string
): Promise<ReconnectResponse> => {
  const response = await client<ReconnectResponse>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}/${accountId}${URL_RECONNECT}`,
    method: 'POST',
  });

  return response.data;
};

export const fetchAccounts = async (
  tenantId: string,
  params?: BaseFilterDto
): Promise<ListResponse<Account>> => {
  const response = await client<ListResponse<Account>>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}`,
    method: 'GET',
    params,
  });
  return response.data;
};

export const createAccount = async (
  tenantId: string,
  params: AddNullToUndefined<CreateAccountDto>
): Promise<Account> => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}`,
    method: 'POST',
    data: params,
  });
  return response.data;
};

export const updateAccount = async (
  tenantId: string,
  id: string,
  params: AddNullToUndefined<UpdateAccountDto>
): Promise<Account> => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}/${id}`,
    method: 'PATCH',
    data: params,
  });
  return response.data;
};

export const restoreBankFields = async (
  tenantId: string,
  id: string,
  params: RestoreBankFieldsPayload
): Promise<Account> => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}/${id}/restore-bank-fields`,
    method: 'POST',
    data: params,
  });
  return response.data;
};

export const deleteAccount = async (tenantId: string, id: string) => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}/${id}`,
    method: 'DELETE',
  });
  return response.data;
};

export const syncAccounts = async (tenantId: string) => {
  const response = await client<Account>({
    url: `${BASE}${API}${V1}${URL_TENANTS}/${tenantId}${URL_ACCOUNTS}${URL_SYNC}`,
    method: 'GET',
  });
  return response.data;
};
